import {
  MovieGridWithTabs,
  MovieTabDateExtraProps,
  MovieTabProps,
} from "@picovico/ui";
import {
  isShowBookable,
  isShowViewable,
  sortAdvanceBookingFirst,
} from "@picovico/ui/constant/enums";
import {
  convertMinutesToHours,
  convertTimeTo12HourFormat,
  getLocalStorageData,
} from "@picovico/utils";
import {
  useGetComingSoonQuery,
  useGetNextReleaseQuery,
  useGetNowShowingQuery,
} from "@store/services/movie";
import { useCallback, useState } from "react";

import { getPageRoutes } from "@pageRoutes";
import { differenceInDays, format } from "date-fns";
import { minBy } from "lodash";
import { BiMoviePlay } from "react-icons/bi";
import { RiTimerLine } from "react-icons/ri";
import { useAppSelector } from "store";
import { ShowInfo } from "types/movie";

function NowShowingUpcoming({
  showTimeList,
  showNowShowingDateSelector,
  dateExtraProps,
  showTabIcon = false,
  prioritizeAdvanceBooking = false,
  ...props
}: Omit<MovieTabProps, "tabs"> & {
  showTimeList?: boolean;
  showNowShowingDateSelector?: boolean;
  dateExtraProps?: MovieTabDateExtraProps;
  showTabIcon?: boolean;
  /** List advance-booking movies first in the Now Showing tab. */
  prioritizeAdvanceBooking?: boolean;
}) {
  const { company_idx } = useAppSelector((state) => state.config);
  const { cinema_hall } = useAppSelector((state) => state.activeCinemaHall);
  const { advanceShowDays } = useAppSelector((state) => state.cineSetting);

  const [currentDate, setCurrentDate] = useState(
    format(new Date(), "yyyy-MM-dd"),
  );

  const {
    isFetching,
    isLoading,
    data: nextRelease,
  } = useGetNextReleaseQuery(
    { company_idx, cinema_hall },
    {
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    },
  );
  const { data: comingSoon } = useGetComingSoonQuery({
    company_idx,
    cinema_hall: cinema_hall ?? getLocalStorageData("cinema_hall"),
  });

  const {
    isLoading: isNowShowingLoading,
    data,
    isFetching: isNowShowingFetching,
    refetch,
  } = useGetNowShowingQuery(
    {
      // Use show_date when date selector is enabled, otherwise use advance_days
      ...(showNowShowingDateSelector
        ? { show_date: currentDate }
        : { advance_days: true }),
      cinema_hall: cinema_hall,
    },
    {
      skip: !cinema_hall,
    },
  );

  const nowShowingMovies =
    prioritizeAdvanceBooking && data?.data
      ? sortAdvanceBookingFirst(data.data, (m) => new Date(m.release_date))
      : data?.data;

  const upcomingMovies = Array.from(
    new Map(
      [...(nextRelease?.data || []), ...(comingSoon?.data || [])].map((m) => [
        m.idx,
        m,
      ]),
    ).values(),
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleDateChange = useCallback(
    (date: Date) => {
      setCurrentDate(format(date, "yyyy-MM-dd"));
      refetch();
    },
    [refetch],
  );

  const getStartDate = (show: ShowInfo[]) => {
    const earliest = minBy(show, (s) => new Date(s.show_start_date_time || 0));
    return earliest?.show_start_date_time
      ? format(new Date(earliest.show_start_date_time), "yyyy-MM-dd")
      : "";
  };

  return (
    <MovieGridWithTabs
      {...props}
      tabs={[
        {
          title: "Now Showing",
          isLoading: isNowShowingLoading || isNowShowingFetching,
          showDateSelector: showNowShowingDateSelector,
          onDateChange: showNowShowingDateSelector
            ? handleDateChange
            : undefined,
          dateSelectorMaxDays: advanceShowDays,
          dateSelectorAlignment: dateExtraProps?.dateSelectorAlignment,
          dateFilterProps: dateExtraProps?.dateFilterProps,
          icon: showTabIcon ? <BiMoviePlay color="#DB322B" /> : null,
          movies:
            nowShowingMovies?.map(
              ({
                idx,
                grade,
                duration,
                poster,
                showinfo,
                release_date,
                genre,
                tag,
                roll_quality,
                ...item
              }) => ({
                title: item.name,
                poster,
                grade,
                genre,
                tag,
                roll_quality,
                duration: convertMinutesToHours(duration),
                idx,
                releaseDate: format(new Date(release_date), "MMMM d yyyy"),
                videoLink: item.trailer,
                detailLink: getPageRoutes(
                  "movie_detail",
                  {
                    movieIdx: idx,
                    cinemaHallIdx: cinema_hall as string,
                  },
                  {
                    ...(showNowShowingDateSelector && { date: currentDate }),
                    startDate: getStartDate(showinfo),
                  },
                ),
                movieShowTimes:
                  showinfo
                    ?.slice()
                    .sort((a, b) =>
                      a.show_start_date_time.localeCompare(
                        b.show_start_date_time,
                      ),
                    )
                    .map((i) => ({
                      ...i,
                      start_time: convertTimeTo12HourFormat(
                        i.show_start_date_time.slice(11, 16),
                      ),
                      available: isShowBookable(i.show_status),
                      viewable: isShowViewable(i.show_status, i.show_ended, i.seat_view_allowed),
                      showLink: getPageRoutes("audi_detail", {
                        movieIdx: idx,
                        showIdx: i.show_idx,
                      }),
                    })) || [],
                isNowShowing: true,
                showTimeList,
              }),
            ) || [],
        },
        {
          title: "Upcoming Movies",
          icon: showTabIcon ? <RiTimerLine color="#007AC0" /> : null,
          movies: upcomingMovies?.map(
            ({
              idx,
              name,
              release_date,
              grade,
              genre,
              tag,
              poster,
              duration,
              trailer,
              movieinfo,
            }) => {
              const releaseDateObj = new Date(release_date);
              const daysLeft = differenceInDays(releaseDateObj, today);

              let ribbonText: string | undefined;
              if (daysLeft > 0) {
                ribbonText = `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;
              } else if (daysLeft === 0) {
                ribbonText = "Releasing Today";
              }
              return {
                idx,
                title: name,
                grade,
                genre,
                tag,
                poster,
                detailLink: getPageRoutes(
                  "movie_detail",
                  {
                    movieIdx: idx,
                    cinemaHallIdx:
                      cinema_hall ?? getLocalStorageData("cinema_hall"),
                  },
                  {
                    startDate: movieinfo?.show_start_date
                      ? format(movieinfo?.show_start_date, "yyyy-MM-dd")
                      : "",
                  },
                ),
                duration: convertMinutesToHours(duration),
                releaseDate: format(releaseDateObj, "MMMM d yyyy"),
                videoLink: trailer,
                isNowShowing: false,
                ribbonText: ribbonText,
              };
            },
          ),
          isLoading: isLoading || isFetching,
        },
      ]}
    />
  );
}

NowShowingUpcoming.defaultProps = {
  showTimeList: true,
  showNowShowingDateSelector: false,
};

export default NowShowingUpcoming;
